import { useState, useEffect, useRef } from "react";
import { gsap } from "../../lib/gsap";
import Container from "../layout/Container";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const underlineRef = useRef(null);
  const navRef = useRef(null);
  const headerRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const hamburgerRef = useRef(null);

  const links = [
    { name: "Overview", href: "#overview" },
    { name: "Autonomy", href: "#autonomy" },
    { name: "Solution", href: "#solution" },
    { name: "Infrastructure", href: "#infrastructure" },
    { name: "Contact", href: "#contact" },
  ];

  /* ================= HEADER SCROLL ================= */

  useEffect(() => {
    gsap.fromTo(
      headerRef.current,
      { y: -80, opacity: 0 },
      { y: 0, opacity: 1, duration: 1, ease: "power3.out" }
    );

    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* ================= MOBILE MENU ANIMATION ================= */

  useEffect(() => {
    if (!mobileMenuRef.current) return;

    if (menuOpen) {
      gsap.to(mobileMenuRef.current, {
        y: 0,
        opacity: 1,
        duration: 0.5,
        ease: "power3.out",
      });

      gsap.to(hamburgerRef.current.children[0], {
        rotate: 45,
        y: 6,
        backgroundColor: "#ffffff",
      });

      gsap.to(hamburgerRef.current.children[1], {
        opacity: 0,
      });

      gsap.to(hamburgerRef.current.children[2], {
        rotate: -45,
        y: -6,
        backgroundColor: "#ffffff",
      });

      document.body.style.overflow = "hidden";
    } else {
      gsap.to(mobileMenuRef.current, {
        y: "-100%",
        opacity: 0,
        duration: 0.4,
        ease: "power3.inOut",
      });

      gsap.to(hamburgerRef.current.children, {
        rotate: 0,
        y: 0,
        opacity: 1,
        backgroundColor: scrolled ? "#0f172a" : "#ffffff",
      });

      document.body.style.overflow = "auto";
    }
  }, [menuOpen, scrolled]);

  /* ================= DESKTOP UNDERLINE ================= */

  const moveUnderline = (e) => {
    if (!navRef.current) return;

    const navRect = navRef.current.getBoundingClientRect();
    const linkRect = e.currentTarget.getBoundingClientRect();

    gsap.to(underlineRef.current, {
      x: linkRect.left - navRect.left,
      width: linkRect.width,
      duration: 0.3,
      ease: "power2.out",
    });
  };

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? "bg-white/90 backdrop-blur-md border-b border-slate-200"
            : "bg-gradient-to-b from-black/70 to-transparent"
        }`}
      >
        <Container>
          <div className="flex items-center justify-between h-20">

            {/* Logo */}
            <div
              className={`text-lg font-semibold tracking-tight transition-colors duration-300 ${
                scrolled ? "text-slate-900" : "text-white"
              }`}
            >
              NE<span className="text-[color:var(--color-accent)]">ORAQAI</span>
            </div>

            {/* Desktop Nav */}
            <nav
              ref={navRef}
              className={`hidden md:flex relative gap-10 text-sm items-center transition-colors duration-300 ${
                scrolled ? "text-slate-600" : "text-white/80"
              }`}
            >
              {links.map((link, i) => (
                <a
                  key={i}
                  href={link.href}
                  onMouseEnter={moveUnderline}
                  className="hover:text-[color:var(--color-accent)] transition-colors duration-300"
                >
                  {link.name}
                </a>
              ))}

              <span
                ref={underlineRef}
                className="absolute bottom-0 h-[2px] bg-[color:var(--color-accent)] w-0"
              />
            </nav>

            {/* CTA Desktop */}
            <div className="hidden md:block">
              <a
                href="#contact"
                className={`px-5 py-2 rounded-full transition-all duration-300 ${
                  scrolled
                    ? "bg-[color:var(--color-accent)] text-white hover:opacity-90"
                    : "border border-white text-white hover:bg-white hover:text-black"
                }`}
              >
                Get Started
              </a>
            </div>

            {/* Hamburger (Mobile) */}
            <button
              ref={hamburgerRef}
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden flex flex-col gap-1.5 z-50"
            >
              <span
                className={`w-6 h-0.5 transition-all ${
                  scrolled ? "bg-slate-900" : "bg-white"
                }`}
              />
              <span
                className={`w-6 h-0.5 transition-all ${
                  scrolled ? "bg-slate-900" : "bg-white"
                }`}
              />
              <span
                className={`w-6 h-0.5 transition-all ${
                  scrolled ? "bg-slate-900" : "bg-white"
                }`}
              />
            </button>

          </div>
        </Container>
      </header>

      {/* Mobile Menu */}
      <div
        ref={mobileMenuRef}
        className="fixed top-0 left-0 w-full h-screen bg-slate-950 text-white flex flex-col items-center justify-center gap-10 text-2xl -translate-y-full opacity-0 z-40"
      >
        {links.map((link, i) => (
          <a
            key={i}
            href={link.href}
            onClick={() => setMenuOpen(false)}
            className="hover:text-[color:var(--color-accent)] transition-colors duration-300"
          >
            {link.name}
          </a>
        ))}
      </div>
    </>
  );
}